<template>
  <div class="voice-note-chat">
    <div ref="thread" class="voice-note-thread">
      <div v-if="!messages.length && !statusText" class="voice-note-empty">
        <h2>Voice note chat</h2>
        <p>
          Press record, speak, then press stop. The recording is sent as a voice
          note and the reply shows up in this chat. Typing is not available
          here.
        </p>
      </div>

      <div
        v-for="message in messages"
        :key="message.id"
        :class="['bubble-row', message.role]"
      >
        <div class="bubble">
          <div class="bubble-label">
            {{ message.role === 'user' ? 'You' : 'AI' }}
          </div>
          <div class="bubble-text">{{ message.content }}</div>
        </div>
      </div>

      <div v-if="statusText" class="voice-note-status">{{ statusText }}</div>
      <div v-if="errorText" class="voice-note-error">{{ errorText }}</div>
    </div>

    <div class="voice-note-controls">
      <button
        class="record-button"
        :class="{ recording: isRecording }"
        type="button"
        :disabled="isSending"
        @click="toggleRecording"
      >
        <span class="record-dot" aria-hidden="true"></span>
        {{ buttonLabel }}
      </button>
      <p class="record-hint">{{ hintText }}</p>
    </div>
  </div>
</template>

<script>
import { sendVoiceNote } from '@/api/voiceNote'

const MAX_RECORD_SECONDS = 60

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return ''
  }
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg'
  ]
  return types.find((type) => MediaRecorder.isTypeSupported(type)) || ''
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = String(reader.result || '')
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () =>
      reject(reader.error || new Error('Could not read recording'))
    reader.readAsDataURL(blob)
  })
}

export default {
  name: 'VoiceNoteChatScreen',
  data() {
    return {
      messages: [],
      isRecording: false,
      isSending: false,
      errorText: '',
      statusText: '',
      elapsedSeconds: 0,
      mediaRecorder: null,
      chunks: [],
      timerId: null,
      stream: null,
      nextId: 1
    }
  },
  computed: {
    buttonLabel() {
      if (this.isSending) return 'Sending'
      if (this.isRecording) return 'Stop and send'
      return 'Record Voice Note'
    },
    hintText() {
      if (this.isRecording) {
        const minutes = String(Math.floor(this.elapsedSeconds / 60)).padStart(
          2,
          '0'
        )
        const seconds = String(this.elapsedSeconds % 60).padStart(2, '0')
        return `Recording ${minutes}:${seconds}. Press stop when you are done.`
      }
      return 'Record only. There is no text box on this page.'
    }
  },
  methods: {
    toggleRecording() {
      if (this.isSending) return
      if (this.isRecording) {
        this.stopRecording()
        return
      }
      this.startRecording()
    },
    async startRecording() {
      this.errorText = ''
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.errorText = 'This browser cannot record audio.'
        return
      }
      if (typeof MediaRecorder === 'undefined') {
        this.errorText = 'This browser cannot record audio.'
        return
      }

      try {
        this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      } catch (permissionError) {
        console.error(permissionError)
        this.errorText =
          'Microphone permission is required to record a voice note.'
        return
      }

      const mimeType = pickMimeType()
      let recorder
      try {
        recorder = mimeType
          ? new MediaRecorder(this.stream, { mimeType })
          : new MediaRecorder(this.stream)
      } catch (recorderError) {
        console.error(recorderError)
        this.releaseStream()
        this.errorText = 'This browser cannot record audio.'
        return
      }
      this.chunks = []
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size) this.chunks.push(event.data)
      }
      recorder.onstop = () => {
        const blobType = recorder.mimeType || mimeType || 'audio/webm'
        const blob = new Blob(this.chunks, { type: blobType })
        this.releaseStream()
        this.sendRecording(blob, blobType)
      }
      recorder.start()
      this.mediaRecorder = recorder
      this.isRecording = true
      this.elapsedSeconds = 0
      this.timerId = setInterval(() => {
        this.elapsedSeconds += 1
        if (this.elapsedSeconds >= MAX_RECORD_SECONDS) this.stopRecording()
      }, 1000)
    },
    stopRecording() {
      if (this.timerId) {
        clearInterval(this.timerId)
        this.timerId = null
      }
      this.isRecording = false
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop()
      }
    },
    async sendRecording(blob, mimeType) {
      if (!blob || !blob.size) {
        this.errorText = 'The recording was empty. Try again.'
        return
      }

      this.isSending = true
      this.statusText = 'Sending voice note…'
      this.errorText = ''
      this.scrollThread()

      try {
        const audioBase64 = await blobToBase64(blob)
        const history = this.messages.map((message) => ({
          role: message.role,
          content: message.content
        }))
        const result = await sendVoiceNote({
          audioBase64,
          mimeType,
          history
        })
        this.messages.push({
          id: this.nextId++,
          role: 'user',
          content: result.transcript || 'Voice note'
        })
        this.messages.push({
          id: this.nextId++,
          role: 'assistant',
          content: result.reply || ''
        })
      } catch (error) {
        this.errorText = error.message || 'Could not send the voice note.'
      } finally {
        this.isSending = false
        this.statusText = ''
        this.scrollThread()
      }
    },
    releaseStream() {
      if (!this.stream) return
      this.stream.getTracks().forEach((track) => track.stop())
      this.stream = null
    },
    scrollThread() {
      this.$nextTick(() => {
        const thread = this.$refs.thread
        if (thread) thread.scrollTop = thread.scrollHeight
      })
    }
  },
  beforeDestroy() {
    if (this.timerId) clearInterval(this.timerId)
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.onstop = null
      this.mediaRecorder.stop()
    }
    this.releaseStream()
  }
}
</script>

<style scoped>
.voice-note-chat {
  display: flex;
  flex-direction: column;
  height: 100vh;
  min-height: 420px;
  background: #f6f7f4;
}

.voice-note-thread {
  flex: 1;
  overflow-y: auto;
  padding: 28px 24px 12px;
}

.voice-note-empty {
  max-width: 460px;
  margin: 48px auto 0;
  text-align: center;
  color: #4d5b4f;
}

.voice-note-empty h2 {
  margin: 0 0 8px;
  color: #00610f;
  font-family: Montserrat, sans-serif;
  font-size: 22px;
  font-weight: 600;
}

.voice-note-empty p {
  margin: 0;
  line-height: 1.5;
  font-size: 14px;
}

.bubble-row {
  display: flex;
  margin-bottom: 12px;
}

.bubble-row.user {
  justify-content: flex-end;
}

.bubble-row.assistant {
  justify-content: flex-start;
}

.bubble {
  max-width: min(560px, 80%);
  padding: 10px 14px;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.bubble-row.user .bubble {
  background: #00610f;
  color: #ffffff;
}

.bubble-label {
  margin-bottom: 4px;
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.7;
}

.bubble-text {
  white-space: pre-wrap;
  line-height: 1.45;
  font-size: 15px;
}

.voice-note-status,
.voice-note-error {
  margin: 8px auto 0;
  max-width: 560px;
  text-align: center;
  font-size: 13px;
}

.voice-note-status {
  color: #5d6b5e;
}

.voice-note-error {
  color: #9b2c2c;
}

.voice-note-controls {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 16px 22px;
  background: #ffffff;
  border-top: 1px solid #e4e8e2;
}

.record-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 220px;
  height: 52px;
  padding: 0 22px;
  border: 0;
  border-radius: 999px;
  background: #00610f;
  color: #ffffff;
  font-family: Montserrat, sans-serif;
  font-size: 15px;
  line-height: 52px;
  text-align: center;
  cursor: pointer;
}

.record-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.record-button.recording {
  background: #9b2c2c;
}

.record-dot {
  position: absolute;
  left: 22px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ffffff;
}

.record-button.recording .record-dot {
  animation: pulse 1s ease-in-out infinite;
}

.record-hint {
  margin: 0;
  color: #6d776e;
  font-size: 12px;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}

@media (max-width: 768px) {
  .voice-note-chat {
    height: 100svh;
    height: 100dvh;
    max-height: 100dvh;
    min-height: 0;
    overflow: hidden;
  }

  .voice-note-thread {
    min-height: 0;
    padding: 16px 16px 8px;
  }

  .voice-note-empty {
    margin-top: 20px;
  }

  .voice-note-controls {
    flex-shrink: 0;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  }
}
</style>
